import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { 
  LayoutDashboard, 
  Layers, 
  Wrench, 
  Square, 
  Settings, 
  Users, 
  Mail, 
  FileText,
  LogOut, 
  ChevronRight,
  Menu,
  X,
  Calendar,
  ArrowLeftRight
} from 'lucide-react';
import { useState } from 'react';
import NotificationBell from '../NotificationBell';

const LOGO_URL = "/logo.png";

const sidebarLinks = [
  { path: '/admin', icon: LayoutDashboard, label: 'Dashboard', exact: true },
  { path: '/admin/devis', icon: FileText, label: 'Tous les devis' },
  { path: '/admin/planning', icon: Calendar, label: 'Planning' },
  { path: '/admin/materiaux', icon: Layers, label: 'Matières' },
  { path: '/admin/faconnage', icon: Wrench, label: 'Façonnage' },
  { path: '/admin/chants', icon: Square, label: 'Chants' },
  { path: '/admin/services', icon: Settings, label: 'Services' },
  { path: '/admin/utilisateurs', icon: Users, label: 'Utilisateurs' },
  { path: '/admin/contacts', icon: Mail, label: 'Contacts' },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  const isActive = (path, exact = false) => {
    if (exact) return location.pathname === path;
    return location.pathname.startsWith(path) && path !== '/admin';
  };

  const getBreadcrumb = () => {
    const path = location.pathname;
    if (path === '/admin') return 'Dashboard';
    const link = sidebarLinks.find(l => l.path !== '/admin' && path.startsWith(l.path));
    return link?.label || '';
  };

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex">
      {/* Sidebar - Desktop */}
      <aside className="hidden lg:flex flex-col w-64 bg-[#0A0A0A] border-r border-white/5 fixed h-full">
        {/* Logo */}
        <div className="p-6 border-b border-white/5">
          <Link to="/admin">
            <img src={LOGO_URL} alt="Granipal" className="h-12 w-auto" />
          </Link>
        </div>

        {/* Admin Badge */}
        <div className="p-6 border-b border-white/5">
          <div className="flex items-center gap-2">
            <span className="px-2 py-1 bg-[#D4AF37]/20 text-[#D4AF37] text-xs uppercase tracking-wider font-bold">
              Admin
            </span>
          </div>
          <p className="text-white font-semibold mt-2">{user?.contact_name}</p>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4 space-y-1 overflow-y-auto">
          {sidebarLinks.map((link) => {
            const Icon = link.icon;
            const active = link.exact ? location.pathname === link.path : isActive(link.path);
            return (
              <Link
                key={link.path}
                to={link.path}
                className={`flex items-center gap-3 px-4 py-3 transition-all duration-300 ${
                  active
                    ? 'bg-[#5D1F1F]/20 text-[#D4AF37] border-l-2 border-[#D4AF37]'
                    : 'text-white/60 hover:text-white hover:bg-white/5'
                }`}
                data-testid={`admin-sidebar-${link.label.toLowerCase().replace(/\s/g, '-')}`}
              >
                <Icon className="w-5 h-5" />
                <span className="text-sm">{link.label}</span>
              </Link>
            );
          })}
        </nav>

        {/* Back to Site */}
        <div className="p-4 border-t border-white/5 space-y-1">
          <Link
            to="/dashboard"
            className="flex items-center gap-3 px-4 py-3 w-full text-white/60 hover:text-white hover:bg-[#D4AF37]/10 transition-colors rounded"
            data-testid="switch-to-dashboard"
          >
            <ArrowLeftRight className="w-5 h-5" />
            <span className="text-sm">Basculer vers Dashboard Pro</span>
          </Link>
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 px-4 py-3 w-full text-white/60 hover:text-red-400 transition-colors"
            data-testid="admin-logout-btn"
          >
            <LogOut className="w-5 h-5" />
            <span className="text-sm">Déconnexion</span>
          </button>
        </div>
      </aside>

      {/* Mobile Header */}
      <div className="lg:hidden fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A] border-b border-white/5">
        <div className="flex items-center justify-between p-4">
          <div className="flex items-center gap-3">
            <Link to="/admin">
              <img src={LOGO_URL} alt="Granipal" className="h-10 w-auto" />
            </Link>
            <span className="px-2 py-1 bg-[#D4AF37]/20 text-[#D4AF37] text-xs uppercase tracking-wider font-bold">
              Admin
            </span>
          </div>
          <div className="flex items-center gap-2">
            <NotificationBell />
            <button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="p-2"
            >
              {isMobileMenuOpen ? (
                <X className="w-6 h-6 text-white" />
              ) : (
                <Menu className="w-6 h-6 text-white" />
              )}
            </button>
          </div>
        </div>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <div className="bg-[#0A0A0A] border-b border-white/5 pb-4 max-h-[80vh] overflow-y-auto">
            <nav className="space-y-1 px-2">
              {sidebarLinks.map((link) => {
                const Icon = link.icon;
                const active = link.exact ? location.pathname === link.path : isActive(link.path);
                return (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={() => setIsMobileMenuOpen(false)}
                    className={`flex items-center gap-3 px-4 py-3 ${
                      active ? 'text-[#D4AF37]' : 'text-white/60'
                    }`}
                  >
                    <Icon className="w-5 h-5" />
                    <span className="text-sm">{link.label}</span>
                  </Link>
                );
              })}
            </nav>
            <div className="px-2 mt-2 pt-2 border-t border-white/5">
              <button
                onClick={handleLogout}
                className="flex items-center gap-3 px-4 py-3 w-full text-white/60"
              >
                <LogOut className="w-5 h-5" />
                <span className="text-sm">Déconnexion</span>
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Main Content */}
      <main className="flex-1 lg:ml-64 min-h-screen">
        {/* Top Bar */}
        <div className="hidden lg:flex items-center justify-between bg-[#0A0A0A] border-b border-white/5 px-8 py-4 sticky top-0 z-40">
          {/* Breadcrumb */}
          <div className="flex items-center gap-2 text-sm">
            <Link to="/admin" className="text-white/40 hover:text-white transition-colors">
              Administration
            </Link>
            {getBreadcrumb() !== 'Dashboard' && (
              <>
                <ChevronRight className="w-4 h-4 text-white/20" />
                <span className="text-white">{getBreadcrumb()}</span>
              </>
            )}
          </div>
          {/* Notifications */}
          <NotificationBell />
        </div>

        {/* Page Content */}
        <div className="p-4 lg:p-8 pt-20 lg:pt-8">
          <Outlet />
        </div>
      </main>
    </div>
  );
}
